import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import {
  useChangePresentation,
  useWriteCareUpdate,
  type CarePresentation,
} from "@/lib/api/care";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { CodeChip } from "./care-primitives";
import { CareUpdateForm } from "./care-update-form";

interface WriteCareUpdateDialogProps {
  menteeId: string;
  open: boolean;
  onClose: () => void;
  /** When set, the dialog finalizes a presentation change instead of a standalone update. */
  presentation?: CarePresentation;
}

export function WriteCareUpdateDialog({
  menteeId,
  open,
  onClose,
  presentation,
}: WriteCareUpdateDialogProps) {
  const { toast } = useToast();
  const writeUpdate = useWriteCareUpdate(menteeId);
  const changePresentation = useChangePresentation(menteeId);
  const pending = writeUpdate.isPending || changePresentation.isPending;

  const onError = () =>
    toast({ title: "Could not save care update", variant: "destructive" });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-text-primary">
            {presentation ? "Change presentation" : "Write care update"}
          </DialogTitle>
        </DialogHeader>
        {presentation ? (
          <div className="flex items-center gap-2">
            <CodeChip code={presentation.code} />
            <p className="font-heading text-base font-bold text-text-primary">
              {presentation.description}
            </p>
          </div>
        ) : null}
        <CareUpdateForm
          menteeId={menteeId}
          presentation={presentation}
          pending={pending}
          onSubmit={(values) => {
            if (presentation) {
              changePresentation.mutate(
                { ...values, presentation },
                {
                  onSuccess: () => {
                    trackEvent(
                      PostHogEvents.CARE_PAGE_PRESENTATION_CHANGE_SUBMITTED,
                      { mentee_id: menteeId, ...presentation },
                    );
                    onClose();
                  },
                  onError,
                },
              );
              return;
            }
            writeUpdate.mutate(values, {
              onSuccess: () => {
                trackEvent(PostHogEvents.CARE_PAGE_CARE_UPDATE_SUBMITTED, {
                  mentee_id: menteeId,
                });
                onClose();
              },
              onError,
            });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
